import { DotLottie } from 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web@0.71.0/+esm';
import { getAxisAlignedBoundingRect } from './dotlottie-bounds.js';
import { shareUrl, getCoupon } from './result-actions.js';
import { getResultLottieFile } from './zodiac-lottie-map.js';

const FONT_FAMILY = 'TaipeiSansTC';
const FONT_URL = document.querySelector('link[href*="TaipeiSansTCBeta"]')?.href
  || Array.from(document.styleSheets).reduce((found, sheet) => {
    if (found) return found;
    try {
      for (const rule of sheet.cssRules) {
        if (rule.cssText?.includes('TaipeiSansTC')) {
          const m = rule.cssText.match(/url\(['"]?([^'")\s]+TaipeiSansTCBeta[^'")\s]*)['"]?\)/);
          if (m) return m[1];
        }
      }
    } catch (_) { }
    return null;
  }, null);

let fontReady = null;
function ensureFont() {
  if (fontReady) return fontReady;
  if (document.fonts) {
    fontReady = document.fonts.ready;
    if (FONT_URL) {
      const ff = new FontFace(FONT_FAMILY, `url('${FONT_URL}')`);
      ff.load().then(f => document.fonts.add(f)).catch(() => { });
    }
  } else {
    fontReady = Promise.resolve();
  }
  return fontReady;
}

// 按鈕百分比佔位（相對於 animationSize，之後可調整）
const BTN_FALLBACKS = {
  'buy-0.png': { cx: 0.225, cy: 0.845, w: 0.22, h: 0.055 },
  'play-0.png': { cx: 0.500, cy: 0.920, w: 0.22, h: 0.055 },
  'share-0.png': { cx: 0.775, cy: 0.845, w: 0.22, h: 0.055 },
  'view more-0.png': { cx: 0.500, cy: 0.880, w: 0.45, h: 0.055 },
};
const SNAP_PLAYBACK_IDLE_MS = 300;
const SNAP_PLAYBACK_RETRY_MS = 80;
const SNAP_PLAYBACK_RETRY_LIMIT = 6;
const SNAP_ALIGN_TOLERANCE_PX = 20;

// 2026-10 星座導引：handle 為 PHASE 1.8/1.9 已驗證的 October published product handles。
// key 統一小寫，與 quiz-intro.js 產出的 result（小寫 zodiac）一致，使 per-zodiac 導購可命中。
const ZODIAC_PRODUCT_HANDLES = {
  aquarius: '哥斯大黎加-藍火山莊園-藝伎-厭氧日曬-淺焙',
  pisces: '哥斯大黎加-多塔-女神莊園-藝伎-蜜處理',
  aries: '衣索比亞-古吉-烏啦嘎-水洗',
  taurus: '哥斯大黎加-卡內特-海頓-葡萄乾蜜處理-淺焙',
  gemini: '衣索比亞-耶加雪菲-艾瑞莎-日曬-淺焙',
  cancer: '哥倫比亞-蘇利亞莊園-荔枝-水洗',
  leo: '哥斯大黎加-卡內特-莫札特-葡萄乾蜜處理',
  virgo: '哥倫比亞-天職莊園-蘇丹汝魅-水洗',
  libra: '哥倫比亞-歡聲莊園-玫瑰-水洗共同發酵',
  scorpio: '印尼-大象漫步-紫蜜果萃-厭氧日曬-淺焙',
  sagittarius: '肯亞-麒麟亞加-pb-水洗',
  capricorn: '哥倫比亞-安地斯晨霧-50-藝伎-日曬',
};

function setZodiacProductRedirect(result) {
  const handle = ZODIAC_PRODUCT_HANDLES[result];
  if (!handle) return;

  const productPath = `/products/${handle}`;
  window.redirectLink = `/discount/100?redirect=${encodeURIComponent(productPath)}`;
}

class LottieResult extends HTMLElement {
  connectedCallback() {
    const result = new URLSearchParams(window.location.search).get('result');
    const name = new URLSearchParams(window.location.search).get('name') || '';
    let src = this.dataset.src;
    const fallbackId = this.dataset.fallback || '';
    const pageKey = this.dataset.page || '';

    if (pageKey === 'page2') setZodiacProductRedirect(result);

    if (!src) return;

    if (result && pageKey && pageKey !== 'none') {
      const fileName = getResultLottieFile(result, pageKey);
      if (fileName) {
        src = src.replace(/[^/]+\.lottie(\?.*)?$/, (_, qs) => fileName + (qs || ''));
      }
    }

    ensureFont();
    requestAnimationFrame(() => this._play(src, result, fallbackId, name));
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._removeSnapListener?.();
    clearTimeout(this._snapTimer);
    clearTimeout(this._snapRetryTimer);
    this._dotLottie?.destroy();
    this._nameElement?.remove();
    this._btnOverlays?.forEach(({ el }) => el.remove());
  }

  _play(src, animationId, fallbackId, name) {
    const canvas = this.querySelector('.lottie-result__player');
    if (!canvas) return;

    this._name = name;
    this._canvas = canvas;
    if (getComputedStyle(this).position === 'static') this.style.position = 'relative';

    // 建立名字 overlay
    this._nameElement = this._createNameElement(name);

    // 預載按鈕圖片並建立 overlay
    this._btnImages = {};
    this.querySelectorAll('.lottie-segment__btn').forEach(btn => {
      const layerId = btn.dataset.layerId;
      const imgEl = btn.querySelector('img');
      if (!layerId || !imgEl) return;
      const img = new Image();
      img.src = imgEl.src;
      this._btnImages[layerId] = img;
    });

    const resizeCanvas = () => {
      const w = canvas.clientWidth * window.devicePixelRatio;
      const h = canvas.clientHeight * window.devicePixelRatio;
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
    };
    resizeCanvas();

    this._dotLottie = new DotLottie({
      canvas,
      src,
      autoplay: false,
      loop: false,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: {
        devicePixelRatio: window.devicePixelRatio,
        useFrameInterpolation: false,
        freezeOnOffscreen: true,
      },
    });

    this._dotLottie.addEventListener('render', () => this._updateOverlays());
    this._dotLottie.addEventListener('frame', ({ currentFrame }) => this._handleFrame(currentFrame));
    this._dotLottie.addEventListener('load', () => this._handleLoad(animationId, fallbackId));

    this._dotLottie.addEventListener('loadError', () => {
      this.querySelector('.lottie-result__error')?.removeAttribute('hidden');
    });

    this._dotLottie.addEventListener('renderError', () => {
      this.querySelector('.lottie-result__error')?.removeAttribute('hidden');
    });

    this._resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
      this._overlaysPositioned = false;
      this._dotLottie?.resize();
      this._scheduleSnapCheck();
    });
    this._resizeObserver.observe(canvas);

    const scrollEl = canvas.closest('[style*="scroll"]') || canvas.closest('#main') || canvas.closest('#MainContent') || window;
    this._snapScrollEl = scrollEl;
    this._snapWrapper = this.closest('.lottie-segment-wrapper');

    if (this._snapWrapper) {
      // segment 頁要等 wrapper 貼齊 scroll 容器頂端後才播放，避免滑動過程中先播掉。
      const onSnapScroll = () => this._scheduleSnapCheck();
      scrollEl.addEventListener('scroll', onSnapScroll, { passive: true });
      window.addEventListener('resize', onSnapScroll);
      this._removeSnapListener = () => {
        scrollEl.removeEventListener('scroll', onSnapScroll);
        window.removeEventListener('resize', onSnapScroll);
      };
    }
  }

  _handleLoad(animationId, fallbackId) {
    if (!this._didSelectAnimation) {
      const ids = this._dotLottie?.manifest?.animations?.map((animation) => animation.id) || [];
      const targetId = ids.includes(animationId)
        ? animationId
        : (ids.includes(fallbackId) ? fallbackId : ids[0]);

      if (!targetId) {
        this.querySelector('.lottie-result__error')?.removeAttribute('hidden');
        return;
      }

      this._didSelectAnimation = true;
      if (ids.length === 1 && targetId === ids[0]) {
        this._initPlayback();
        return;
      }

      this._dotLottie.loadAnimation(targetId);
      return;
    }

    this._initPlayback();
  }

  _handleFrame(currentFrame) {
    if (this._triggerFrame == null) return;
    if (currentFrame < this._triggerFrame) return;
    if (this._loopJumping) return;

    this._loopJumping = true;
    this._dotLottie.setFrame(this._loopStart);
    this._dotLottie.play();
    requestAnimationFrame(() => { this._loopJumping = false; });
  }

  // 延後檢查播放時機，確保 scroll-snap 貼齊完畢後才開始 segment 動畫。
  _scheduleSnapCheck() {
    clearTimeout(this._snapTimer);
    clearTimeout(this._snapRetryTimer);
    this._snapTimer = setTimeout(() => this._checkSnapPlaybackWithRetry(), SNAP_PLAYBACK_IDLE_MS);
  }

  _checkSnapPlaybackWithRetry(retryCount = 0) {
    this._checkSnapPlayback();
    if (this._didStartPlayback) return;
    if (retryCount >= SNAP_PLAYBACK_RETRY_LIMIT) return;

    clearTimeout(this._snapRetryTimer);
    this._snapRetryTimer = setTimeout(() => {
      this._checkSnapPlaybackWithRetry(retryCount + 1);
    }, SNAP_PLAYBACK_RETRY_MS);
  }

  _isSnapAligned() {
    // 非 segment 用途沒有 snap wrapper，維持載入完成後直接播放。
    if (!this._snapWrapper) return true;

    const wrapperRect = this._snapWrapper.getBoundingClientRect();
    if (this._snapScrollEl === window) {
      return Math.abs(wrapperRect.top) <= SNAP_ALIGN_TOLERANCE_PX;
    }

    const scrollRect = this._snapScrollEl.getBoundingClientRect();
    return Math.abs(wrapperRect.top - scrollRect.top) <= SNAP_ALIGN_TOLERANCE_PX;
  }

  _checkSnapPlayback() {
    if (!this._didInitPlayback || this._didStartPlayback) return;
    if (!this._isSnapAligned()) return;

    this._didStartPlayback = true;
    this._dotLottie.play();
  }

  _initPlayback() {
    if (this._didInitPlayback) return;

    const totalFrames = this._dotLottie.totalFrames;
    const fps = totalFrames / this._dotLottie.duration;
    const markers = this._dotLottie.markers() || [];
    const loopMarker = markers.find((marker) => marker.name === 'loop-start');

    // this._loopStart = loopMarker ? loopMarker.time : Math.max(0, totalFrames - 40);
    // this._triggerFrame = Math.floor(totalFrames * 0.95);
    this._loopStart = loopMarker ? loopMarker.time :
      Math.floor(fps * 7);
    this._triggerFrame = Math.min(totalFrames - 1,
      Math.floor(fps * 9));
    this._didInitPlayback = true;
    // 先停在首幀，實際播放交給 snap 對齊檢查觸發。
    this._dotLottie.setFrame(0);
    requestAnimationFrame(() => this._checkSnapPlaybackWithRetry());
  }

  // 設置 Overlay 淡入淡出
  _setOverlayVisible(visible) {
    const opacity = visible ? '1' : '0';
    if (this._nameElement) this._nameElement.style.opacity = opacity;
    this._btnOverlays?.forEach(({ el }) => { el.style.opacity = opacity; });
  }

  _getLayerRect(layerName) {
    const rect = getAxisAlignedBoundingRect(this._dotLottie?.getLayerBoundingBox(layerName));
    if (!rect) return null;

    const dpr = window.devicePixelRatio;
    return {
      x: rect.x / dpr,
      y: rect.y / dpr,
      width: rect.width / dpr,
      height: rect.height / dpr,
    };
  }

  _createNameElement(name) {
    if (!name) return null;
    const el = document.createElement('div');
    el.className = 'lottie-result__name';
    el.textContent = name;
    Object.assign(el.style, {
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      lineHeight: '1',
      color: '#434343',
      fontFamily: `'${FONT_FAMILY}', sans-serif`,
      zIndex: '2',
      opacity: '0',
      transition: 'opacity 0.25s',
    });
    this.append(el);
    return el;
  }

  _createBtnOverlays() {
    if (this._btnOverlays) return;
    this._btnOverlays = [];
    this.querySelectorAll('.lottie-segment__btn').forEach(btn => {
      const overlay = document.createElement(btn.tagName.toLowerCase() === 'button' ? 'button' : 'a');
      overlay.className = 'lottie-result__btn-overlay';
      if (overlay.tagName === 'A') overlay.href = btn.getAttribute('href') || '#';
      if (btn.dataset.action) overlay.dataset.action = btn.dataset.action;
      overlay.dataset.layerId = btn.dataset.layerId;

      const img = this._btnImages[btn.dataset.layerId];
      if (img) {
        const imgEl = document.createElement('img');
        imgEl.src = img.src;
        Object.assign(imgEl.style, { width: '100%', height: '100%', objectFit: 'contain', display: 'block' });
        overlay.append(imgEl);
      }

      Object.assign(overlay.style, {
        position: 'absolute',
        display: 'block',
        zIndex: '2',
        cursor: 'pointer',
        background: 'transparent',
        border: 'none',
        padding: '0',
        opacity: '0',
        transition: 'opacity 0.25s',
      });

      if (btn.dataset.action === 'share-url') {
        overlay.addEventListener('click', (e) => {
          e.preventDefault();
          shareUrl();
        });
      } else if (btn.dataset.action === 'get-coupon') {
        overlay.addEventListener('click', (e) => {
          e.preventDefault();
          getCoupon();
        });
      }

      this.append(overlay);
      this._btnOverlays.push({ el: overlay, layerId: btn.dataset.layerId });
    });
  }

  _updateOverlays() {
    const canvas = this._canvas;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    const canvasLeft = canvasRect.left - hostRect.left;
    const canvasTop = canvasRect.top - hostRect.top;
    const dpr = window.devicePixelRatio;

    if (this._overlaysPositioned) {
      if (!this._overlayShown && this._didStartPlayback && this._isSnapAligned()) {
        this._overlayShown = true;
        this._setOverlayVisible(true);
      }
      return;
    }

    let didPositionOverlay = false;

    // 名字
    if (this._nameElement && this._name) {
      const layerRect = this._getLayerRect('user_name.png');
      let left, top, width, height;

      if (layerRect) {
        const scaleX = canvasRect.width / (canvas.width / dpr);
        const scaleY = canvasRect.height / (canvas.height / dpr);
        left = canvasLeft + layerRect.x * scaleX;
        top = canvasTop + layerRect.y * scaleY;
        width = layerRect.width * scaleX;
        height = layerRect.height * scaleY;
      } else {
        this._nameElement.style.display = 'none';
        return;
      }

      this._nameElement.style.display = 'flex';
      Object.assign(this._nameElement.style, {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        fontSize: `${Math.max(10, height * 0.7)}px`,
      });
      didPositionOverlay = true;
    }

    // 按鈕
    if (!this._btnOverlays) this._createBtnOverlays();
    this._btnOverlays?.forEach(({ el, layerId }) => {
      const layerRect = this._getLayerRect(layerId);
      let left, top, width, height;

      if (layerRect) {
        const scaleX = canvasRect.width / (canvas.width / dpr);
        const scaleY = canvasRect.height / (canvas.height / dpr);
        left = canvasLeft + layerRect.x * scaleX;
        top = canvasTop + layerRect.y * scaleY;
        width = layerRect.width * scaleX;
        height = layerRect.height * scaleY;
      } else {
        const f = BTN_FALLBACKS[layerId];
        if (!f) return;
        width = f.w * canvasRect.width;
        height = f.h * canvasRect.height;
        left = canvasLeft + f.cx * canvasRect.width - width / 2;
        top = canvasTop + f.cy * canvasRect.height - height / 2;
      }

      Object.assign(el.style, {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      });
      didPositionOverlay = true;
    });
    this._overlaysPositioned = didPositionOverlay;

    if (!this._overlayShown && this._didStartPlayback && this._isSnapAligned()) {
      this._overlayShown = true;
      this._setOverlayVisible(true);
    }
  }
}

customElements.define('lottie-result', LottieResult);
